/**
 * Every sheet of a nesting run in ONE file.
 *
 * The factory asked for the whole run in a single file, sheets laid out in a
 * grid the way their RIP shows artboards: each sheet keeps its exact paper
 * size and its red paper frame, sheets are separated by a clear gap, and a
 * small caption above every sheet names it (file stem, roster row, garments).
 * Per-row runs get one grid row per roster entry so a size's sheets sit side
 * by side; whole-order runs get a near-square grid.
 *
 * The per-sheet files are still produced — this file only adds to the zip.
 * Sheets are inlined as `<g transform="translate()">` copies of their markup
 * (not nested `<svg>` viewports, which CorelDRAW drops), with every id
 * namespaced per sheet so clipPaths cannot collide between sheets.
 */
import { namespaceSvgIds } from './logoVectorize';
import { mmToPt } from './units';

export interface CombinedSheetEntry {
  /** A finished sheet SVG (buildSheetSvg / buildCutlinesSvg output). */
  svg: string;
  /** Roster-row label in per-row mode; sheets with the same group share a grid row. */
  group?: string | null;
  /** Caption printed above the sheet (file stem etc.); empty for none. */
  caption?: string;
}

export interface CombinedSheetsOptions {
  paperWidthPt: number;
  paperLengthPt: number;
  /** One grid row per group (per-roster-row runs). */
  perRow?: boolean;
  /** Clear space between sheets and around the page, pt. Default 3 cm or 5% of the paper width. */
  gapPt?: number;
  /** Root attributes (namespaces); default xmlns + xlink. */
  rootAttrs?: string;
}

interface ParsedSheet {
  inner: string;
  scaleX: number;
  scaleY: number;
}

const DEFAULT_ROOT_ATTRS =
  'xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"';

const round = (n: number) => Math.round(n * 1000) / 1000;

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Strip a sheet SVG down to its inner markup; scale when its viewBox is not the paper size. */
function parseSheet(svg: string, paperWidthPt: number, paperLengthPt: number): ParsedSheet | null {
  const open = svg.match(/<svg[^>]*>/i);
  if (!open || open.index === undefined) return null;
  const close = svg.lastIndexOf('</svg>');
  if (close < 0) return null;
  const inner = svg.slice(open.index + open[0].length, close);
  const vb = open[0].match(/viewBox\s*=\s*"([^"]+)"/i);
  let scaleX = 1;
  let scaleY = 1;
  if (vb) {
    const parts = vb[1]
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (parts.length === 4 && parts[2] > 0 && parts[3] > 0) {
      scaleX = paperWidthPt / parts[2];
      scaleY = paperLengthPt / parts[3];
    }
  }
  return { inner, scaleX, scaleY };
}

/** Grid rows of entry indices. */
export function combinedSheetRows(
  entries: Array<Pick<CombinedSheetEntry, 'group'>>,
  perRow: boolean,
  paperWidthPt: number,
  paperLengthPt: number
): number[][] {
  const n = entries.length;
  if (!n) return [];
  if (perRow) {
    const rows: number[][] = [];
    let last: string | null | undefined;
    entries.forEach((e, i) => {
      const g = e.group ?? null;
      if (!rows.length || g !== last) {
        rows.push([i]);
        last = g;
      } else rows[rows.length - 1].push(i);
    });
    return rows;
  }
  // Near-square page: columns so that the grid's aspect is close to 1.
  const columns = Math.min(
    n,
    Math.max(1, Math.round(Math.sqrt((n * paperLengthPt) / paperWidthPt)))
  );
  const rows: number[][] = [];
  for (let i = 0; i < n; i += columns) {
    rows.push(Array.from({ length: Math.min(columns, n - i) }, (_, k) => i + k));
  }
  return rows;
}

/** Null when there is nothing to lay out or no sheet could be read. */
export function buildCombinedSheetsSvg(
  entries: CombinedSheetEntry[],
  opts: CombinedSheetsOptions
): string | null {
  const W = opts.paperWidthPt;
  const L = opts.paperLengthPt;
  if (!entries.length || !(W > 0) || !(L > 0)) return null;
  const parsed = entries.map((e) => parseSheet(e.svg, W, L));
  if (parsed.every((p) => !p)) return null;

  const gap = opts.gapPt ?? Math.max(mmToPt(30), W * 0.05);
  const hasCaptions = entries.some((e) => e.caption);
  // Caption band above each sheet: ~1.5 cm, scaled with the paper, only when used.
  const captionPt = hasCaptions ? Math.min(mmToPt(40), Math.max(mmToPt(8), W * 0.03)) : 0;
  const fontPt = captionPt * 0.6;

  const rows = combinedSheetRows(entries, !!opts.perRow, W, L);
  const columns = Math.max(...rows.map((r) => r.length));
  const totalW = gap + columns * (W + gap);
  const rowPitch = captionPt + L + gap;
  const totalL = gap + rows.length * rowPitch;

  const body = rows
    .map((row, r) =>
      row
        .map((index, c) => {
          const p = parsed[index];
          if (!p) return '';
          const entry = entries[index];
          const x = gap + c * (W + gap);
          const y = gap + r * rowPitch + captionPt;
          const caption = entry.caption
            ? `<text x="${round(x)}" y="${round(y - captionPt * 0.3)}" font-family="Arial, Helvetica, sans-serif"` +
              ` font-size="${round(fontPt)}" fill="#000000">${escapeText(entry.caption)}</text>`
            : '';
          const scale =
            p.scaleX !== 1 || p.scaleY !== 1 ? ` scale(${round(p.scaleX)} ${round(p.scaleY)})` : '';
          return (
            caption +
            `<g data-sheet="${index + 1}" transform="translate(${round(x)} ${round(y)})${scale}">` +
            namespaceSvgIds(p.inner, `s${index + 1}_`) +
            '</g>'
          );
        })
        .join('')
    )
    .join('');

  const w = round(totalW);
  const l = round(totalL);
  return (
    `<svg ${opts.rootAttrs || DEFAULT_ROOT_ATTRS} width="${w}pt" height="${l}pt" viewBox="0 0 ${w} ${l}">` +
    body +
    '</svg>'
  );
}
